import { FC, useState } from "react";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Checkbox } from "@capacit-com/lara-law-shared-ui";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileText,
  FolderPlus,
  FolderTree,
  MessageSquare,
  PlusCircle,
  RefreshCw,
  SkipForward,
  User,
} from "lucide-react";
import { looksLikeId } from "@/lib/regex";
import { cn } from "@/lib/utils";
import { showSuccessToast } from "@/components/custom-toasts/toasts";
import { SyncReportDocumentEntry, SyncResults } from "@/types/sync.types";
import { UploadItem } from "@/components/upload/types/upload-item";

interface SyncReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  results: SyncResults;
  /**
   * When provided (admin/owner only), enables the inline delete UI for files
   * listed in results.missingFilesForAdminReview.
   */
  onDelete?: (documentIds: string[]) => Promise<void>;
  /**
   * Upload feedback items (e.g. Excel sheet-level feedback) to show inline
   * in the report instead of opening a separate dialog.
   */
  uploadFeedback?: UploadItem[];
}

// ─── CSV export ─────────────────────────────────────────────────────────────

const escapeCsvCell = (value: string): string => {
  const needsQuoting =
    value.includes(",") || value.includes('"') || value.includes("\n");
  if (!needsQuoting) return value;
  return `"${value.replace(/"/g, '""')}"`;
};

const buildCsvRows = (
  results: SyncResults,
  uploadFeedback?: UploadItem[]
): string => {
  const rows: string[] = [];

  // Metadata preamble so the CSV is self-describing when opened standalone.
  // IDs are included so developers can identify exact resources from a
  // customer-submitted report.
  const timestamp = new Date().toLocaleString("da-DK");
  const ctx = results.reportContext;
  if (ctx?.workspaceName)
    rows.push(
      `${escapeCsvCell("Workspace")},${escapeCsvCell(ctx.workspaceName)}`
    );
  if (ctx?.workspaceId)
    rows.push(
      `${escapeCsvCell("Workspace ID")},${escapeCsvCell(ctx.workspaceId)}`
    );
  if (ctx?.folderName)
    rows.push(`${escapeCsvCell("Mappe")},${escapeCsvCell(ctx.folderName)}`);
  if (ctx?.folderId)
    rows.push(`${escapeCsvCell("Mappe ID")},${escapeCsvCell(ctx.folderId)}`);
  if (ctx?.initiatedByName)
    rows.push(
      `${escapeCsvCell("Igangsat af")},${escapeCsvCell(ctx.initiatedByName)}`
    );
  if (ctx?.initiatedById)
    rows.push(
      `${escapeCsvCell("Bruger ID")},${escapeCsvCell(ctx.initiatedById)}`
    );
  rows.push(`${escapeCsvCell("Dato")},${escapeCsvCell(timestamp)}`);
  rows.push(""); // blank separator

  rows.push(escapeCsvCell("Dokumenter"));
  const header = ["Status", "Filnavn", "Dokument ID", "Mappe", "Ejer"]
    .map(escapeCsvCell)
    .join(",");
  rows.push(header);

  const addRow = (
    status: string,
    name: string,
    id = "",
    folder = "",
    owner = ""
  ) =>
    rows.push([status, name, id, folder, owner].map(escapeCsvCell).join(","));

  // Per-file rows — most actionable first
  for (const doc of results.missingFilesForAdminReview)
    addRow(
      "Ikke tilgængelig",
      doc.document.name,
      doc.document.id,
      doc.folderPath,
      doc.ownerName ?? ""
    );
  for (const doc of results.skippedMissingDocuments)
    addRow(
      "Sprunget over (ingen adgang)",
      doc.document.name,
      doc.document.id,
      doc.folderPath,
      doc.ownerName ?? ""
    );
  for (const error of results.errors) addRow("Fejl", error);
  for (const doc of results.syncedDocuments)
    addRow(
      "Synkroniseret",
      doc.document.name,
      doc.document.id,
      doc.folderPath,
      doc.ownerName ?? ""
    );
  for (const doc of results.noUpdateDocuments)
    addRow(
      "Allerede opdateret",
      doc.document.name,
      doc.document.id,
      doc.folderPath,
      doc.ownerName ?? ""
    );
  for (const doc of results.newDocumentEntries)
    addRow(
      "Ny fil tilføjet",
      doc.fileName,
      doc.sharepointFileId ?? "",
      doc.folderPath,
      doc.ownerName ?? ""
    );
  if (results.newFoldersCreated > 0)
    addRow(
      "Nye mapper oprettet",
      `${results.newFoldersCreated} mappe(r) oprettet`
    );

  // Upload feedback section (Excel sheet-level feedback)
  const feedbackItems = (uploadFeedback ?? []).filter(
    (u) => u.feedback && Object.keys(u.feedback).length > 0
  );
  if (feedbackItems.length > 0) {
    rows.push(""); // blank separator
    rows.push(escapeCsvCell("Upload feedback"));
    rows.push(["Filnavn", "Sheet", "Besked"].map(escapeCsvCell).join(","));
    for (const upload of feedbackItems) {
      for (const [sheet, message] of Object.entries(upload.feedback!)) {
        rows.push([upload.name, sheet, message].map(escapeCsvCell).join(","));
      }
    }
  }

  return rows.join("\n");
};

const toFilenamePart = (value: string): string =>
  value
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9æøå-]/g, "")
    .slice(0, 40);

const downloadCsv = (results: SyncResults, uploadFeedback?: UploadItem[]) => {
  const csv = buildCsvRows(results, uploadFeedback);
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  const timestamp = new Date()
    .toISOString()
    .slice(0, 16)
    .replace("T", "_")
    .replace(":", "-");

  const parts = ["synkroniseringsrapport"];
  if (results.reportContext?.workspaceName)
    parts.push(toFilenamePart(results.reportContext.workspaceName));
  if (results.reportContext?.folderName)
    parts.push(toFilenamePart(results.reportContext.folderName));
  parts.push(timestamp);

  anchor.href = url;
  anchor.download = parts.join("_") + ".csv";
  anchor.click();
  URL.revokeObjectURL(url);
};

// ─── Dialog ─────────────────────────────────────────────────────────────────

const SyncReportDialog: FC<SyncReportDialogProps> = ({
  isOpen,
  onClose,
  results,
  onDelete,
  uploadFeedback,
}) => {
  // Mirror results locally so deletions from the admin section are reflected
  // in the CSV download — the prop never changes after the dialog opens.
  const [liveResults, setLiveResults] = useState<SyncResults>(results);

  const handleFilesDeleted = (deletedIds: string[]) => {
    setLiveResults((prev) => ({
      ...prev,
      missingFilesForAdminReview: prev.missingFilesForAdminReview.filter(
        (e) => !deletedIds.includes(e.document.id)
      ),
    }));
  };

  const hasErrors = liveResults.errors.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Synkroniseringsrapport</DialogTitle>
          <DialogDescription>
            Oversigt over hvad der skete under synkroniseringen.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {/* Synced */}
          {liveResults.existingDocumentsSynced > 0 && (
            <Section
              icon={<RefreshCw className="h-4 w-4 text-primary" />}
              title={`${liveResults.existingDocumentsSynced} fil${liveResults.existingDocumentsSynced === 1 ? "" : "er"} synkroniseret`}
            />
          )}

          {/* No update needed */}
          {liveResults.existingDocumentsNoUpdate > 0 && (
            <Section
              icon={<CheckCircle2 className="h-4 w-4 text-success" />}
              title={`${liveResults.existingDocumentsNoUpdate} fil${liveResults.existingDocumentsNoUpdate === 1 ? "" : "er"} er allerede opdateret`}
            />
          )}

          {/* New files added */}
          {liveResults.newDocumentsAdded > 0 && (
            <Section
              icon={<PlusCircle className="h-4 w-4 text-success" />}
              title={`${liveResults.newDocumentsAdded} ny${liveResults.newDocumentsAdded === 1 ? " fil" : "e filer"} tilføjet fra SharePoint/OneDrive`}
            />
          )}

          {/* New folders created */}
          {liveResults.newFoldersCreated > 0 && (
            <Section
              icon={<FolderPlus className="h-4 w-4 text-success" />}
              title={`${liveResults.newFoldersCreated} ny${liveResults.newFoldersCreated === 1 ? " mappe" : "e mapper"} oprettet`}
            />
          )}

          {/* Missing files – admin/owner: inline delete UI */}
          {liveResults.missingFilesForAdminReview.length > 0 && (
            <AdminMissingFilesSection
              entries={liveResults.missingFilesForAdminReview}
              onDelete={onDelete}
              onFilesDeleted={handleFilesDeleted}
            />
          )}

          {/* Missing files – upload-only: read-only with owner info */}
          {liveResults.skippedMissingDocuments.length > 0 && (
            <ReadOnlyMissingFilesSection
              entries={liveResults.skippedMissingDocuments}
            />
          )}

          {/* Errors */}
          {hasErrors && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 space-y-2">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                <span className="text-sm font-medium text-destructive ">
                  {liveResults.errors.length} fejl opstod under synkronisering
                </span>
              </div>
              <ul className="ml-6 space-y-1">
                {liveResults.errors.map((error, i) => (
                  <li key={i} className="text-xs text-destructive ">
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Upload feedback (e.g. Excel sheet-level feedback) */}
          {uploadFeedback && uploadFeedback.length > 0 && (
            <UploadFeedbackSection items={uploadFeedback} />
          )}
        </DialogBody>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button
            variant="outline"
            onClick={() => downloadCsv(liveResults, uploadFeedback)}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Download CSV
          </Button>
          <Button onClick={onClose}>Luk</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Admin missing-files section (with inline delete) ───────────────────────

interface AdminMissingFilesSectionProps {
  entries: SyncReportDocumentEntry[];
  onDelete?: (documentIds: string[]) => Promise<void>;
  /** Called after successful deletion so the parent can update its live results */
  onFilesDeleted?: (deletedIds: string[]) => void;
}

const AdminMissingFilesSection: FC<AdminMissingFilesSectionProps> = ({
  entries,
  onDelete,
  onFilesDeleted,
}) => {
  const [visibleEntries, setVisibleEntries] = useState(entries);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedIds(
      selectedIds.size === visibleEntries.length
        ? new Set()
        : new Set(visibleEntries.map((e) => e.document.id))
    );
  };

  const handleDelete = async () => {
    if (!onDelete || selectedIds.size === 0) return;
    setIsDeleting(true);
    try {
      const deletedIds = Array.from(selectedIds);
      await onDelete(deletedIds);
      const count = deletedIds.length;
      const remaining = visibleEntries.filter(
        (e) => !selectedIds.has(e.document.id)
      );
      setVisibleEntries(remaining);
      setSelectedIds(new Set());
      onFilesDeleted?.(deletedIds);
      showSuccessToast(
        `${count} fil${count === 1 ? "" : "er"} slettet fra workspace`,
        "Succes!"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  if (visibleEntries.length === 0) return null;

  return (
    <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center space-x-2">
        <SkipForward className="h-4 w-4 text-warning shrink-0" />
        <span className="text-sm font-medium text-warning ">
          {visibleEntries.length} fil
          {visibleEntries.length === 1 ? "" : "er"} ikke tilgængelig
          {visibleEntries.length === 1 ? "" : "e"} på SharePoint/OneDrive
        </span>
      </div>

      <p className="text-xs text-warning ml-6">
        Disse filer kunne ikke tilgås — de er muligvis slettet, eller du mangler
        adgang. Vælg de filer du ønsker at fjerne fra workspace.
      </p>

      {/* Select all */}
      {onDelete && (
        <div className="flex items-center space-x-2 ml-6 pb-1 border-b border-warning/30 ">
          <Checkbox
            id="missing-select-all"
            checked={
              visibleEntries.length > 0 &&
              selectedIds.size === visibleEntries.length
            }
            onCheckedChange={handleSelectAll}
          />
          <label
            htmlFor="missing-select-all"
            className="text-xs font-medium cursor-pointer text-warning "
          >
            Vælg alle ({selectedIds.size}/{visibleEntries.length})
          </label>
        </div>
      )}

      {/* File list */}
      <ul className="space-y-1">
        {visibleEntries.map((entry) => {
          const isSelected = selectedIds.has(entry.document.id);
          return (
            <li
              key={entry.document.id}
              className={cn(
                "flex items-start space-x-3 p-2 rounded-lg border transition-colors",
                isSelected
                  ? "bg-warning/10 border-warning/30 "
                  : "border-transparent hover:bg-warning/10/50 "
              )}
            >
              {onDelete && (
                <Checkbox
                  id={entry.document.id}
                  checked={isSelected}
                  onCheckedChange={() => handleToggle(entry.document.id)}
                  className="mt-0.5 shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <FileText className="h-3.5 w-3.5 text-warning shrink-0" />
                  <label
                    htmlFor={entry.document.id}
                    className="text-xs font-medium cursor-pointer truncate text-warning "
                  >
                    {entry.document.name}
                  </label>
                </div>
                {entry.folderPath && (
                  <div className="flex items-center space-x-2 mt-0.5 text-xs text-warning ">
                    <FolderTree className="h-3 w-3 shrink-0" />
                    <span className="truncate">{entry.folderPath}</span>
                  </div>
                )}
                {entry.ownerName && (
                  <div className="flex items-center space-x-2 mt-0.5 text-xs text-warning ">
                    <User className="h-3 w-3 shrink-0" />
                    <span className="truncate">Ejer: {entry.ownerName}</span>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Delete action */}
      {onDelete && (
        <div className="flex justify-end pt-1">
          <Button
            variant="destructive"
            size="sm"
            disabled={selectedIds.size === 0 || isDeleting}
            onClick={handleDelete}
          >
            {isDeleting
              ? "Sletter..."
              : `Slet ${selectedIds.size} fil${selectedIds.size === 1 ? "" : "er"} fra workspace`}
          </Button>
        </div>
      )}
    </div>
  );
};

// ─── Upload-only missing-files section (read-only) ──────────────────────────

const ReadOnlyMissingFilesSection: FC<{
  entries: SyncReportDocumentEntry[];
}> = ({ entries }) => (
  <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 space-y-2">
    <div className="flex items-center space-x-2">
      <SkipForward className="h-4 w-4 text-warning shrink-0" />
      <span className="text-sm font-medium text-warning ">
        {entries.length} fil{entries.length === 1 ? "" : "er"} sprunget over –
        ikke fundet eller manglende adgang
      </span>
    </div>
    <p className="text-xs text-warning ml-6">
      Disse filer kunne ikke tilgås med din nuværende konto og er sprunget over.
      Kontakt ejeren hvis du har brug for adgang.
    </p>
    <ul className="ml-6 space-y-1 mt-1">
      {entries.map((entry) => (
        <li
          key={entry.document.id}
          className="text-xs text-warning flex flex-col gap-0.5"
        >
          <span className="font-medium">{entry.document.name}</span>
          {entry.folderPath && (
            <span className="text-warning ">Mappe: {entry.folderPath}</span>
          )}
          {entry.ownerName && (
            <span className="text-warning ">Ejer: {entry.ownerName}</span>
          )}
        </li>
      ))}
    </ul>
  </div>
);

// ─── Upload feedback section ─────────────────────────────────────────────────

const UploadFeedbackSection: FC<{ items: UploadItem[] }> = ({ items }) => {
  const itemsWithFeedback = items.filter(
    (u) => u.feedback && Object.keys(u.feedback).length > 0
  );
  if (itemsWithFeedback.length === 0) return null;

  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex items-center space-x-2">
        <MessageSquare className="h-4 w-4 text-primary shrink-0" />
        <span className="text-sm font-medium">
          Upload feedback – {itemsWithFeedback.length} fil
          {itemsWithFeedback.length === 1 ? "" : "er"} med feedback
        </span>
      </div>
      <p className="text-xs text-muted-foreground ml-6">
        Feedback fra backend-behandling af uploadede filer.
      </p>
      <Accordion type="single" collapsible className="ml-2">
        {itemsWithFeedback.map((upload, idx) => (
          <AccordionItem value={`fb-${idx}`} key={upload.id}>
            <AccordionTrigger className="hover:no-underline py-2">
              <div className="flex items-center gap-2">
                <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="text-xs font-medium">{upload.name}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <ul className="ml-2 space-y-2 pt-1">
                {Object.entries(upload.feedback!).map(([key, value]) => {
                  // Strip trailing colon that some keys include (e.g. "failed references:")
                  const label = key.replace(/:$/, "");
                  return (
                    <li
                      key={key}
                      className="border-l-2 border-muted pl-3 space-y-0.5"
                    >
                      <p className="text-xs font-medium text-foreground capitalize">
                        {label}
                      </p>
                      <p
                        className={cn(
                          "text-xs text-muted-foreground break-all",
                          looksLikeId(value) && "font-mono"
                        )}
                      >
                        {value.length > 0 ? value : "Ingen feedback"}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

// ─── Summary row ────────────────────────────────────────────────────────────

const Section: FC<{ icon: React.ReactNode; title: string }> = ({
  icon,
  title,
}) => (
  <div className="flex items-center space-x-2 p-2 rounded-lg border bg-muted/30">
    {icon}
    <span className="text-sm">{title}</span>
  </div>
);

export default SyncReportDialog;
